import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeTypeModel, AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ShipownerIcon from "@rightstore/MobiusOverrides/Icons/shipownerIcon";
import TerminalIcon from "@rightstore/MobiusOverrides/Icons/terminalIcon";
import UnionIcon from "@rightstore/MobiusOverrides/Icons/UnionIcon";
import WheelIcon from "@rightstore/MobiusOverrides/Icons/WheelIcon";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    maximumNumberAttributeTypes: number;
    extendedStyles?: ProductAttributesStyles;
    showAllAttributes?: boolean;
}

type Props = OwnProps & HasProduct;

export interface ProductAttributesStyles {
    container?: GridContainerProps;
    attributeTypeGridItem?: GridItemProps;
    attributeValueGridItem?: GridItemProps;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    attributeCategories?: InjectableCss;
    attributeCustomertype?: InjectableCss;
}

export const productAttributesStyles: ProductAttributesStyles = {
    container: {
        gap: 0,
    },
    attributeTypeGridItem: {
        width: [6, 6, 6, 4, 4],
        css: css`
            overflow: hidden;
        `,
    },
    attributeValueGridItem: {
        width: [12],
        css: css`
            overflow: hidden;
            flex-wrap:wrap;          
        `,
    },
    attributeTypeText: {
        weight: "bold",
    },
    attributeCategories: {
        css: css`          
            padding: 2px 12px 2px 12px;
            font-size: 12px;
            color: #003745;
            border-radius: 100px;
            background-color: #E6F3FF;
            margin: 2px 0 2px 4px;
            line-height: 20px;
        `,
    },
    attributeCustomertype: {
        css: css`
            padding: 2px 12px 2px 12px;
            font-size: 12px;
            color: #333333;
            border-radius: 100px;
            background-color: #D9D9D9;
            display: flex;
            align-items: center;
            margin: 2px 0 2px 4px;   
            line-height: 20px;         

            svg {
                height: 16px;
                width: 16px;
                margin-right: 4px;
            }
        `,
    }
};

const ProductAttributes: FC<Props> = ({ product: { attributeTypes }, maximumNumberAttributeTypes, extendedStyles, showAllAttributes = false }) => {
    const [styles] = React.useState(() => mergeToNew(productAttributesStyles, extendedStyles));

    if (!attributeTypes || attributeTypes.length === 0) {
        return null;
    }

    const maximum = Math.min(maximumNumberAttributeTypes, 100);
    const limitedAttributeTypes = attributeTypes.slice(0, maximum);

    const customerTypeIcons = (attributeValue: string) => {
        switch (attributeValue.toString()) {
            case 'Charterers':
                return <WheelIcon />;
            case 'Owners & Managers':
                return <ShipownerIcon />
            case 'Ports & Terminals':
                return <TerminalIcon />
            case 'Financiers':
                return <UnionIcon />
            case 'Traders':
                return <UnionIcon />
            default:
                return "";
        }
    }

    const displayAttributeValues = (attributeType: AttributeTypeModel | null) => {
        return (
            <>
                {attributeType && attributeType.name === "CustomerType" && (
                    <>
                        {attributeType.attributeValues?.map((attributeValue: AttributeValueModel, index: number) => {
                            return (
                                <React.Fragment key={index}>
                                    {showAllAttributes &&
                                        <StyledWrapper  {...styles.attributeCustomertype}>
                                            {customerTypeIcons(attributeValue.valueDisplay)}
                                            <Typography>
                                                {attributeValue.value}
                                            </Typography>
                                        </StyledWrapper>}
                                    {!showAllAttributes && index === 0 &&
                                        <StyledWrapper  {...styles.attributeCustomertype}>
                                            {customerTypeIcons(attributeValue.valueDisplay)}
                                            <Typography>
                                                {attributeValue.value}
                                            </Typography>
                                        </StyledWrapper>}
                                </React.Fragment>
                            )
                        })}
                        {!showAllAttributes && attributeType.attributeValues && attributeType.attributeValues.length > 1 &&
                            <StyledWrapper {...styles.attributeCustomertype}>
                                <Typography>
                                    {attributeType.attributeValues && (
                                        `+ ${attributeType.attributeValues.length - 1}`
                                    )}
                                </Typography>
                            </StyledWrapper>
                        }
                    </>
                )}
                {attributeType && attributeType.name === "Categories" && (
                    <>
                        {attributeType.attributeValues?.map((attributeValue: AttributeValueModel, index: number) => {
                            return (
                                <React.Fragment key={index}>
                                    {showAllAttributes &&
                                        <StyledWrapper  {...styles.attributeCategories}>
                                            {customerTypeIcons(attributeValue.valueDisplay)}
                                            <Typography>
                                                {attributeValue.value}
                                            </Typography>
                                        </StyledWrapper>

                                    }
                                    {!showAllAttributes && index === 0 && (
                                        <StyledWrapper  {...styles.attributeCategories}>
                                            {customerTypeIcons(attributeValue.valueDisplay)}
                                            <Typography>
                                                {attributeValue.value}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </React.Fragment>
                            )
                        })}
                        {!showAllAttributes && attributeType.attributeValues && attributeType.attributeValues.length > 1 &&
                            <StyledWrapper {...styles.attributeCategories}>
                                <Typography>
                                    {attributeType.attributeValues && (
                                        `+ ${attributeType.attributeValues.length - 1}`
                                    )}
                                </Typography>
                            </StyledWrapper>}
                    </>
                )}
            </>
        )



    };


    return (
        <GridContainer {...styles.container}>
            {limitedAttributeTypes.map(attributeType => (
                <React.Fragment key={attributeType.id.toString()}>
                    <GridItem
                        {...styles.attributeValueGridItem}
                        data-test-selector={`attributeValuesFor${attributeType.id}`}
                    >
                        {displayAttributeValues(attributeType)}
                    </GridItem>
                </React.Fragment>
            ))}
        </GridContainer>
    );
};

export default withProduct(ProductAttributes);
